import type {
  AdminRecordListItemDTO,
  PaginatedRecordsResponseDTO,
  RecordListItemDTO,
  RecordsFiltersDTO,
  UpdateRecordsRequestDTO,
} from "@/application/records/dtos/RecordListItemDTO";
import type {
  ContextTokensRequestDTO,
  ContextTokensResponseDTO,
} from "@/application/records/dtos/ContextTokensDTO";
import type {
  MarkdownEndpoint,
  RecordsGatewayPort,
} from "@/application/records/ports/RecordsGatewayPort";
import { FileTypeDomainService } from "@/domain/upload/services/FileTypeDomainService";
import {
  FrontendContainer,
  createDefaultFrontendContainer,
} from "@/infrastructure/di/FrontendContainer";
import { getAuthHeaders } from "@/infrastructure/http/authHeaders";
import { InsightApiAdapter } from "@/infrastructure/records/adapters/InsightApiAdapter";
import {
  RecordApiAdapter,
  type AdminRecordListItemResponse,
  type RecordListItemResponse,
  type RecordsListApiResponse,
} from "@/infrastructure/records/adapters/RecordApiAdapter";
import { ReportRepository } from "@/infrastructure/records/repositories/ReportRepository";
import { normalizeMarkdownValue } from "@/lib/markdown-normalizer";
import type { RecordsMetaBatchRequestOptions } from "@/lib/records-meta-batch";
import type { RecordDetailRequestContext } from "@/lib/record-detail-transport";
import type {
  DetailedReportResponse,
  RecordBatchMetaResponse,
  RecordMetaResponse,
} from "@/lib/types";

interface GetMarkdownOptions {
  version?: string | number | null;
}

const SAFE_MAX_LIMIT = 200;

export class RecordsGateway implements RecordsGatewayPort {
  constructor(
    private readonly recordApiAdapter: RecordApiAdapter,
    private readonly insightApiAdapter: InsightApiAdapter,
    private readonly reportRepository: ReportRepository
  ) {}

  async getRecordsList(limit: number = 1000): Promise<RecordListItemDTO[]> {
    const normalizedLimit = Math.min(limit, SAFE_MAX_LIMIT);
    const response = await this.recordApiAdapter.getRecordsList(
      1,
      normalizedLimit,
      undefined
    );
    const rawRecords = this.extractRecords<RecordListItemResponse>(response);
    return rawRecords.map((record) => this.mapRecordListItem(record));
  }

  async getAdminRecordsList(
    page: number = 1,
    limit: number = 10000,
    filters?: RecordsFiltersDTO
  ): Promise<AdminRecordListItemDTO[]> {
    const aggregated: AdminRecordListItemDTO[] = [];
    const normalizedPage = Math.max(page, 1);
    let remaining = limit > 0 ? limit : Infinity;
    let currentPage = normalizedPage;

    while (remaining > 0) {
      const pageSize = Math.min(remaining, SAFE_MAX_LIMIT);
      const response = await this.getAdminRecordsPaginated(
        currentPage,
        pageSize,
        filters
      );
      const mapped = response.records as AdminRecordListItemDTO[];
      aggregated.push(...mapped);

      if (!response.has_more || mapped.length < pageSize) {
        break;
      }

      currentPage += 1;
      if (Number.isFinite(remaining)) {
        remaining = Math.max(remaining - mapped.length, 0);
      }
    }

    return aggregated;
  }

  async getRecordsPaginated(
    page: number,
    limit: number,
    filters?: RecordsFiltersDTO
  ): Promise<PaginatedRecordsResponseDTO> {
    const normalizedLimit = Math.min(limit, SAFE_MAX_LIMIT);
    const response = await this.recordApiAdapter.getRecordsList(
      page,
      normalizedLimit,
      filters
    );
    const rawRecords = this.extractRecords<RecordListItemResponse>(response);
    return {
      records: rawRecords.map((record) => this.mapRecordListItem(record)),
      total: response.total ?? rawRecords.length,
      remaining_size_mb: response.remaining_size_mb,
      page: response.page ?? page,
      limit: response.limit ?? normalizedLimit,
      has_more:
        response.has_more ??
        (rawRecords.length === normalizedLimit || Boolean(response.next)),
    };
  }

  async getAdminRecordsPaginated(
    page: number,
    limit: number,
    filters?: RecordsFiltersDTO
  ): Promise<PaginatedRecordsResponseDTO> {
    const normalizedLimit = Math.min(limit, SAFE_MAX_LIMIT);
    const response = await this.recordApiAdapter.getAdminRecordsList(
      page,
      normalizedLimit,
      filters
    );
    const rawRecords =
      this.extractRecords<AdminRecordListItemResponse>(response);
    const mapped = rawRecords.map((record) => ({
      ...this.mapRecordListItem(record),
      email: record.email ?? "",
    }));

    return {
      records: mapped,
      total: response.total ?? mapped.length,
      page: response.page ?? page,
      limit: response.limit ?? normalizedLimit,
      has_more:
        response.has_more ??
        (mapped.length === normalizedLimit || Boolean(response.next)),
    };
  }

  async updateRecords(request: UpdateRecordsRequestDTO): Promise<void> {
    await this.insightApiAdapter.updateRecords(request);
  }

  async getRecordMeta(
    recordId: string,
    include?: string[]
  ): Promise<RecordMetaResponse> {
    return this.insightApiAdapter.getRecordMeta(recordId, include);
  }

  async getContextTokens(
    request: ContextTokensRequestDTO
  ): Promise<ContextTokensResponseDTO> {
    if (!request.recordIds || request.recordIds.length === 0) {
      return { totalTokens: 0, ratePer100k: 0 };
    }

    try {
      const response = await this.insightApiAdapter.getContextTokens(
        request.recordIds,
        request.contextType,
        request.projectId
      );

      if (!response) {
        return { totalTokens: 0, ratePer100k: 0 };
      }

      return {
        totalTokens: response.total_tokens ?? 0,
        ratePer100k: response.rate_per_100k ?? 0,
      };
    } catch {
      return { totalTokens: 0, ratePer100k: 0 };
    }
  }

  async getRecordsMetaBatch(
    recordIds: string[],
    options?: RecordsMetaBatchRequestOptions
  ): Promise<Record<string, RecordBatchMetaResponse>> {
    return this.insightApiAdapter.getRecordBatchMeta(recordIds, options);
  }

  async getDetailedRecord(
    recordId: string,
    version?: number | null,
    requestContext?: RecordDetailRequestContext
  ): Promise<DetailedReportResponse> {
    return (await this.insightApiAdapter.getDetailedRecord(
      recordId,
      version ?? undefined,
      requestContext
    )) as DetailedReportResponse;
  }

  async getMarkdownReport(
    recordId: string,
    endpoint: MarkdownEndpoint,
    options?: GetMarkdownOptions
  ): Promise<{ markdown_content: string; version: number; record_id: string }> {
    const parsedVersion = this.parseVersion(options?.version);
    let markdown: string | null = null;

    switch (endpoint) {
      case "qa-report":
        markdown = await this.reportRepository.getQaReport(
          recordId,
          parsedVersion
        );
        break;
      case "persona-report":
        markdown = await this.reportRepository.getPersonaReport(
          recordId,
          parsedVersion
        );
        break;
      case "custom-report":
        markdown = await this.reportRepository.getCustomReport(
          recordId,
          parsedVersion
        );
        break;
      case "jtbd-markdown-report":
      default:
        markdown = await this.reportRepository.getJtbdReport(
          recordId,
          parsedVersion
        );
        break;
    }

    const markdownContent = normalizeMarkdownValue(markdown) ?? "";

    return {
      markdown_content: markdownContent,
      version: parsedVersion ?? 0,
      record_id: recordId,
    };
  }

  async updateMarkdownReport(
    recordId: string,
    endpoint: MarkdownEndpoint,
    content: string,
    version?: number | null
  ): Promise<void> {
    const parsedVersion = this.parseVersion(version);

    switch (endpoint) {
      case "qa-report":
        await this.reportRepository.updateQaReport(
          recordId,
          content,
          parsedVersion
        );
        break;
      case "persona-report":
        await this.reportRepository.updatePersonaReport(
          recordId,
          content,
          parsedVersion
        );
        break;
      case "custom-report":
        await this.reportRepository.updateCustomReport(
          recordId,
          content,
          parsedVersion
        );
        break;
      case "jtbd-markdown-report":
      default:
        await this.reportRepository.updateJtbdReport(
          recordId,
          content,
          parsedVersion
        );
        break;
    }
  }

  async patchTranscriptUtterance(
    recordId: string,
    utteranceId: number,
    newText: string,
    version?: number | null
  ): Promise<void> {
    await this.insightApiAdapter.patchTranscriptUtterance(
      recordId,
      utteranceId,
      newText,
      version
    );
  }

  async patchSpeakerName(
    recordId: string,
    speaker: string,
    speakerName: string,
    version?: number | null
  ): Promise<void> {
    await this.insightApiAdapter.patchSpeakerName(
      recordId,
      speaker,
      speakerName,
      version
    );
  }

  async patchUtteranceSpeaker(
    recordId: string,
    utteranceId: number,
    newSpeaker: string,
    version?: number | null
  ): Promise<void> {
    await this.insightApiAdapter.patchUtteranceSpeaker(
      recordId,
      utteranceId,
      newSpeaker,
      version
    );
  }

  async patchSpeakerRespondent(
    recordId: string,
    speaker: string,
    isRespondent: boolean,
    version?: number | null
  ): Promise<void> {
    await this.insightApiAdapter.patchSpeakerRespondent(
      recordId,
      speaker,
      isRespondent,
      version
    );
  }

  async switchPublicity(recordId: string, isPublic: boolean): Promise<void> {
    await this.insightApiAdapter.switchPublicity(recordId, isPublic);
  }

  async postTranscriptRefine(
    recordId: string,
    version?: number | null
  ): Promise<{ transcript_version: number }> {
    return this.insightApiAdapter.postTranscriptRefine(recordId, version);
  }

  async postQualityRecheck(
    recordId: string,
    version?: number | null
  ): Promise<void> {
    await this.insightApiAdapter.postQualityRecheck(recordId, version);
  }

  private extractRecords<T extends RecordListItemResponse>(
    response: RecordsListApiResponse<T> | null | undefined
  ): T[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response.records) && response.records.length > 0) {
      return response.records;
    }

    if (Array.isArray(response.results) && response.results.length > 0) {
      return response.results;
    }

    return [];
  }

  private mapRecordListItem = (
    record: RecordListItemResponse
  ): RecordListItemDTO => {
    const mappedProject =
      typeof record.project === "string" && record.project !== null
        ? record.project
        : "";

    // Try to get file_path from different possible field names
    // Since RecordListItemResponse has [key: string]: unknown, we can access any field
    let filePath: string | null | undefined = record.file_path;
    
    // Try alternative field names if file_path is not found
    if (!filePath && "file_path" in record) {
      filePath = record["file_path"] as string | null | undefined;
    }
    if (!filePath && "filePath" in record) {
      filePath = record["filePath"] as string | null | undefined;
    }

    return {
      id: record.id,
      name: record.name,
      project: mappedProject,
      type:
        record.type ||
        FileTypeDomainService.inferFileType(
          record.file_extension ?? undefined
        ) ||
        "text",
      status: record.status,
      participants:
        record.participants_count ??
        record.participant_count ??
        record.participants ??
        0,
      participants_count:
        record.participants_count ??
        record.participant_count ??
        record.participants ??
        0,
      creation_date: record.creation_date || undefined,
      modified_date: record.modified_date || undefined,
      content: typeof record.content === "string" ? record.content : "",
      size_mb: record.size_mb ?? 0,
      is_archived: Boolean(record.is_archived),
      is_public: Boolean(record.is_public),
      file_extension: record.file_extension,
      file_name: record.file_name,
      file_path: filePath ?? null,
      duration_sec: record.duration_sec ?? null,
    };
  };

  private parseVersion(version?: string | number | null): number | undefined {
    if (version === null || version === undefined) {
      return undefined;
    }
    if (typeof version === "number") {
      return version;
    }
    const parsed = parseInt(version, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
}

let sharedContainer: FrontendContainer | null = null;
let sharedGateway: RecordsGateway | null = null;

function ensureContainer(container?: FrontendContainer): FrontendContainer {
  if (container) {
    return container;
  }
  if (!sharedContainer) {
    sharedContainer = createDefaultFrontendContainer({
      authHeadersProvider: getAuthHeaders,
    });
  }
  return sharedContainer;
}

export function createRecordsGateway(
  container?: FrontendContainer
): RecordsGateway {
  const resolvedContainer = ensureContainer(container);
  if (container) {
    return new RecordsGateway(
      resolvedContainer.resolveRecordApiAdapter(),
      resolvedContainer.resolveInsightApiAdapter(),
      resolvedContainer.resolveReportRepository()
    );
  }

  if (!sharedGateway) {
    sharedGateway = new RecordsGateway(
      resolvedContainer.resolveRecordApiAdapter(),
      resolvedContainer.resolveInsightApiAdapter(),
      resolvedContainer.resolveReportRepository()
    );
  }

  return sharedGateway;
}
